import axios from "axios";
import {
  CONTENT_HUB_ADAPTER_URL,
  SEARCH_PAGE_SIZE,
  contentHubUrl,
  type ContentHubSource,
} from "./config";
import { contentHubClient, contentHubPublicClient } from "./client";
import type { FilterTreeNode } from "./filter";
import { mapSearchHit } from "./mappers";
import type { ContentHubPage, ContentHubSearchResponse } from "./types";
import type { AxiosInstance } from "axios";

/** Authenticated live search endpoint. */
const SEARCH_PATH = "v1/search";
/** Public, auth-less 24h-delayed search endpoint (demo mode). Same request /
 *  response contract as SEARCH_PATH; served anonymously. */
const DELAYED_SEARCH_PATH = "v1/search/delayed";

/** Thrown when /v1/search rejects the filter tree (HTTP 422). A tree 422 means
 *  the compiled tree is off-contract — a buildTree bug, not a user error — so
 *  callers should surface it and never retry. */
export class ContentHubFilterError extends Error {
  readonly status = 422;
  readonly detail: unknown;
  constructor(detail: unknown) {
    super("Content-hub rejected the filter (422)");
    this.name = "ContentHubFilterError";
    this.detail = detail;
  }
}

/** Thrown when a *cursor* request is rejected (HTTP 422) — a corrupted/expired
 *  opaque token. The caller should restart from page one, not retry. */
export class ContentHubCursorError extends Error {
  readonly status = 422;
  constructor() {
    super("Content-hub rejected the cursor (422) — restart from page one");
    this.name = "ContentHubCursorError";
  }
}

/**
 * Core POST /v1/search call, cursor-paginated. Body is a filter tree; results
 * arrive newest-first (sort=recent). Returns one mapped page plus the opaque
 * `next_cursor` for the next (older) page (null at end of history).
 */
async function postContentHubSearch(
  body: FilterTreeNode,
  {
    size = SEARCH_PAGE_SIZE,
    cursor,
    path = SEARCH_PATH,
    client = contentHubClient,
  }: {
    size?: number;
    cursor?: string;
    /** Adapter search path — SEARCH_PATH (live) or DELAYED_SEARCH_PATH (demo). */
    path?: string;
    /** Which axios client to use — authenticated (live) or public (delayed). */
    client?: AxiosInstance;
  } = {}
): Promise<ContentHubPage> {
  if (!CONTENT_HUB_ADAPTER_URL) {
    throw new Error("VITE_CONTENT_HUB_ADAPTER_URL is not configured");
  }
  const url = new URL(contentHubUrl(path));
  url.searchParams.set("size", String(size));
  url.searchParams.set("sort", "recent");
  // Echo the opaque cursor verbatim; only append it for pages beyond the first.
  if (cursor) url.searchParams.set("cursor", cursor);

  let data: ContentHubSearchResponse;
  try {
    const response = await client.post<ContentHubSearchResponse>(
      url.toString(),
      body
    );
    data = response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      if (err.response.status === 422) {
        // A 422 while paging is a bad cursor (restart); on page one, a bad tree.
        if (cursor) throw new ContentHubCursorError();
        throw new ContentHubFilterError(
          err.response.data ?? err.response.statusText
        );
      }
      throw new Error(
        `Content-hub search failed: ${err.response.status} ${err.response.statusText}`
      );
    }
    throw err;
  }

  return {
    items: (data.results ?? []).map(mapSearchHit),
    nextCursor: data.next_cursor ?? null,
    count: data.count ?? 0,
  };
}

/**
 * Fetch a page of historical events for one or more sources via POST /v1/search
 * (the unfiltered per-view feed). Multiple sources are merged server-side into
 * one newest-first, single-cursor stream.
 */
export async function searchContentHubEvents(
  sources: readonly ContentHubSource[],
  cursor?: string
): Promise<ContentHubPage> {
  return postContentHubSearch({ backend_source: [...sources] }, { cursor });
}

/**
 * Fetch a page of 24h-delayed historical events for one or more sources via the
 * PUBLIC POST /search/delayed endpoint — the anonymous feed shown in demo mode
 * (logged-out or expired access). Same body/response contract as the live feed,
 * cursor-paginated, but served without auth and with a fixed delay. There is no
 * delayed live stream yet, so this is history-only.
 */
export async function searchContentHubDelayed(
  sources: readonly ContentHubSource[],
  cursor?: string
): Promise<ContentHubPage> {
  return postContentHubSearch(
    { backend_source: [...sources] },
    { cursor, path: DELAYED_SEARCH_PATH, client: contentHubPublicClient }
  );
}

/**
 * Fetch a page of filtered events via POST /v1/search with a compiled filter
 * tree. Throws `ContentHubFilterError` on a tree 422, `ContentHubCursorError`
 * on a cursor 422.
 *
 * `size` overrides the default page size (e.g. a calendar day view fetches a
 * whole day in one large page). `delayed` routes to the public 24h-delayed
 * endpoint (demo mode) instead of the authenticated live feed — same tree body
 * and response contract.
 */
export async function searchContentHubByTree(
  tree: FilterTreeNode,
  cursor?: string,
  options: { size?: number; delayed?: boolean } = {}
): Promise<ContentHubPage> {
  const { size, delayed } = options;
  return postContentHubSearch(tree, {
    cursor,
    size,
    ...(delayed
      ? { path: DELAYED_SEARCH_PATH, client: contentHubPublicClient }
      : {}),
  });
}

// ── Live filtering: POST /v1/stream/configure ────────────────────────────────

/** One live filter to attach to the stream: an opaque client id + its tree. */
export interface StreamFilterConfig {
  id: string;
  tree: FilterTreeNode;
}

/** Per-filter ack in the configure response. */
export interface StreamFilterAck {
  id: string;
  subscription_id?: string;
  status?: string;
}

export interface ConfigureResponse {
  connection_id: string;
  filters: StreamFilterAck[];
  feed?: string;
}

/** The connection is gone (socket dropped / adapter restarted). Don't retry —
 *  wait for the next `connected` frame and reconfigure. */
export class ContentHubConnectionGoneError extends Error {
  readonly status = 404;
  constructor() {
    super("Content-hub stream connection is gone (404)");
    this.name = "ContentHubConnectionGoneError";
  }
}

/** The adapter rejected a filter tree (422). Atomic — the connection's previous
 *  filter state is intact; the caller resends the set without the bad id. */
export class ContentHubConfigureError extends Error {
  readonly status = 422;
  /** The offending filter id, if the adapter named it. */
  readonly filterId: string | null;
  readonly detail: unknown;
  constructor(detail: unknown, filterId: string | null) {
    super("Content-hub rejected a live filter (422)");
    this.name = "ContentHubConfigureError";
    this.detail = detail;
    this.filterId = filterId;
  }
}

/**
 * Attach the full desired live-filter set to an open stream (replace semantics).
 * `filters: []` disables everything.
 */
export async function configureContentHubStream(
  connectionId: string,
  filters: StreamFilterConfig[]
): Promise<ConfigureResponse> {
  if (!CONTENT_HUB_ADAPTER_URL) {
    throw new Error("VITE_CONTENT_HUB_ADAPTER_URL is not configured");
  }
  try {
    const response = await contentHubClient.post<ConfigureResponse>(
      contentHubUrl("v1/stream/configure"),
      { connection_id: connectionId, filters }
    );
    return response.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const { status, statusText } = err.response;
      if (status === 404) {
        throw new ContentHubConnectionGoneError();
      }
      if (status === 422) {
        const detail = err.response.data ?? statusText;
        // The adapter names the offending filter id; dig it out best-effort.
        const filterId =
          (detail &&
            typeof detail === "object" &&
            // common shapes: { filter_id }, { detail: "...'id'..." }
            ("filter_id" in detail
              ? String((detail as { filter_id: unknown }).filter_id)
              : null)) ||
          null;
        throw new ContentHubConfigureError(detail, filterId);
      }
      throw new Error(
        `Content-hub configure failed: ${status} ${statusText}`
      );
    }
    throw err;
  }
}
